"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { getPlayerColor, playSound } from "@/app/utils";
import SpellSelection from "@/components/SpellSelection";
import { useAuth } from "@/components/AuthProvider";

export default function SelectPlayer({ gameId }) {
  const [game, setGame] = useState(null);
  const [showSpellSelection, setShowSpellSelection] = useState(false);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    fetch(`/api/games/${gameId}`)
      .then((res) => res.json())
      .then((data) => setGame(data.game))
      .catch((error) => console.error("Error fetching game:", error));
  }, [gameId]);

  // Check if current user already has a player in this game
  const myPlayer = game?.players?.find((p) => p.userId === user?.id);

  const handleSelectPlayer = (player) => {
    // Only allow selecting your own player
    if (player.userId === user?.id) {
      router.push(`/game/${gameId}/${player.id}`);
    }
  };

  const handleJoinGame = () => {
    if (game?.settings?.useSpells) {
      setShowSpellSelection(true);
    } else {
      handleSpellSelected(null);
    }
  };

  const handleSpellSelected = async (selectedSpell) => {
    setJoining(true);
    setError(null);
    try {
      const res = await fetch(`/api/games/${gameId}/join`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          selectedSpell: selectedSpell,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to join game");
      }

      router.push(`/game/${gameId}/${data.id}`);
    } catch (err) {
      setError(err.message);
      setShowSpellSelection(false);
    } finally {
      setJoining(false);
    }
  };

  if (showSpellSelection) {
    return <SpellSelection onSpellSelected={handleSpellSelected} />;
  }

  if (!game || authLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div>Loading...</div>
      </div>
    );
  }

  // If user already has a player in this game, auto-redirect
  if (myPlayer) {
    return (
      <div className="p-8 flex justify-center items-center min-h-screen">
        <div className="text-center">
          <p className="mb-4">You are already in this game as {myPlayer.username}</p>
          <button
            onClick={() => router.push(`/game/${gameId}/${myPlayer.id}`)}
            className="bg-green-600 hover:bg-green-700 px-6 py-3 rounded font-medium"
          >
            Continue Playing
          </button>
        </div>
      </div>
    );
  }

  // If not logged in, show login prompt
  if (!user) {
    return (
      <div className="p-8 flex justify-center items-center min-h-screen">
        <div className="text-center">
          <h1 className="text-xl font-bold mb-4">Login Required</h1>
          <p className="text-gray-400 mb-6">You need to be logged in to join a game.</p>
          <div className="flex gap-4 justify-center">
            <Link
              href="/login"
              className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded font-medium"
            >
              Login
            </Link>
            <Link
              href="/register"
              className="bg-green-600 hover:bg-green-700 px-6 py-3 rounded font-medium"
            >
              Register
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 flex justify-center items-center min-h-screen">
      <div className="">
        <h1 className="text-lg font-bold mb-2">{game.name || `Game ${game.id}`}</h1>

        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-300 px-4 py-2 rounded mb-4">
            {error}
          </div>
        )}

        {game.players.length > 0 && (
          <>
            <p className="text-gray-400 mb-2">Players in game:</p>
            <ul className="space-y-2 mb-6">
              {game.players.map((player) => (
                <li key={player.id} className="flex items-center gap-2">
                  <div
                    className={`w-3 h-3 rounded-full ${getPlayerColor(player.id)}`}
                  />
                  <span>{player.username}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        <button
          onClick={handleJoinGame}
          disabled={joining}
          className="w-full p-4 rounded-lg text-lg bg-green-600 hover:bg-green-700 disabled:bg-green-600/50 font-medium"
        >
          {joining ? "Joining..." : `Join as ${user.username}`}
        </button>

        <Link
          href="/"
          className="block text-center text-gray-400 hover:text-white mt-4"
        >
          Back to games list
        </Link>
      </div>
    </div>
  );
}
